import {Swiper, SwiperSlide} from '@/_application/core/components/ui/slider';
import {convertArrayToMultidimensional} from '@/_application/core/utils/convert-array-to-multidimensional';
import {compareBrandByPosition} from '@/_application/modules/brand/helpers/brand-data';
import {DEFAULT_MAX_ITEMS_PER_ROW_SWIPER} from '@/lib/constants';
import React from 'react';
import {Scrollbar} from 'swiper/modules';
import {HighlightBrand, HighlightBrands} from '@/__generated__/__types__';
import {getBrandUrl} from '@/_application/core/utils/common';
import {productPlaceholder} from '@/lib/placeholders';
import cn from 'classnames';
import {Image} from '@/_application/core/components/ui/image';
import Link from '@/_application/core/components/ui/link';
import {BorderTypeEnum, LayoutType} from '@/_application/modules/brand/model/brand';

const getBreakPointSlide = (configBanner: boolean) => {
    return {
        1280: {
            slidesPerView: configBanner ? 7.5 : 7.55,
            spaceBetween: configBanner ? 20 : 30,
        },
        1024: {
            slidesPerView: configBanner ? 5.8 : 6,
            spaceBetween: 20,
        },
        768: {
            slidesPerView: configBanner ? 4.5 : 4,
            spaceBetween: 10,
        },
    };
};

export interface PopularBrandProps {
    highlightBrands?: HighlightBrands | null;
}

const PopularBrand: React.FC<PopularBrandProps> = ({highlightBrands}) => {
    const homePageConfig = highlightBrands?.homepage_layout ?? {};
    const {layout, limit_qty, row_qty, border_type} = homePageConfig;

    const filteredBrands = (highlightBrands?.items?.filter(brand => brand) ?? []) as HighlightBrand[];
    const sortedBrands = filteredBrands.sort(compareBrandByPosition);
    const slicedHighlightBrands = sortedBrands.slice(0, limit_qty ?? DEFAULT_MAX_ITEMS_PER_ROW_SWIPER);

    if (!slicedHighlightBrands.length) {
        return null;
    }

    let highlightBrandList;
    if (!row_qty || row_qty === 1) {
        highlightBrandList = slicedHighlightBrands;
    } else {
        highlightBrandList = convertArrayToMultidimensional(slicedHighlightBrands, row_qty);
    }

    const brandBorder = cn({
        'rounded-[10px] h-20 w-[128px]': border_type === BorderTypeEnum.SQUARE,
        'rounded-[170px] h-[120px] w-[120px]': border_type === BorderTypeEnum.CIRCLE,
        'rounded-[170px] w-[120px] h-[160px]': border_type === BorderTypeEnum.OVAL,
    });
    const brandStyle =
        'flex select-none items-center justify-center overflow-hidden border border-solid border-KPZ_turquoise transition-opacity hover:opacity-80';

    return (
        <div className="w-full">
            <div className="flex flex-col content-center justify-center rounded-2xl bg-white p-5">
                <h2 className="mb-5 text-[22px] font-extrabold leading-[25px] text-primary">Thương hiệu nổi bật</h2>
                <div className="">
                    <div className="flex gap-5">
                        <Swiper
                            direction="horizontal"
                            scrollbar={{
                                el: '.swiper-scrollbar',
                                draggable: true,
                                dragSize: 'auto',
                            }}
                            modules={[Scrollbar]}
                            breakpoints={getBreakPointSlide((homePageConfig?.banner_image?.length ?? 0) > 0)}
                            className="!pb-8">
                            <>
                                {layout?.value === LayoutType.WITH_BANNER && homePageConfig?.banner_image?.length && (
                                    <SwiperSlide
                                        key={homePageConfig?.banner_image ?? ''}
                                        className="!flex !w-[180px] !items-start !justify-start">
                                        <Link
                                            href={getBrandUrl(homePageConfig?.banner_link ?? '')}
                                            className={`rounded-[10px] border border-solid border-KPZ_turquoise transition-opacity hover:opacity-80`}>
                                            <div className="flex h-[180px] w-[180px] items-center justify-center p-2.5">
                                                <Image
                                                    src={homePageConfig?.banner_image ?? ''}
                                                    alt={'banner-brand'}
                                                    width={180}
                                                    height={180}
                                                />
                                            </div>
                                        </Link>
                                    </SwiperSlide>
                                )}
                                {!row_qty || row_qty === 1 ? (
                                    <>
                                        {(highlightBrandList as HighlightBrand[]).map(item => {
                                            return (
                                                <SwiperSlide className="!w-[128px]" key={item?.id}>
                                                    <Link
                                                        href={getBrandUrl(item?.url ?? '')}
                                                        className={cn(brandBorder, brandStyle)}>
                                                        <div className="flex h-[80px] w-[80px] items-center justify-center p-2.5">
                                                            <Image
                                                                src={item?.img ?? productPlaceholder}
                                                                alt={item?.label ?? 'Logo'}
                                                                width={100}
                                                                height={100}
                                                            />
                                                        </div>
                                                    </Link>
                                                </SwiperSlide>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <>
                                        {(highlightBrandList as HighlightBrand[][])?.map(item => {
                                            const [position1, position2] = item;
                                            return (
                                                <SwiperSlide className="!w-[128px]" key={position1?.id}>
                                                    <div className="flex flex-col items-center justify-center gap-5">
                                                        <Link
                                                            href={getBrandUrl(position1?.url ?? '')}
                                                            className={cn(brandBorder, brandStyle)}>
                                                            <div className="flex h-[80px] w-[80px] items-center justify-center p-2.5">
                                                                <Image
                                                                    src={position1?.img ?? productPlaceholder}
                                                                    alt={position1?.label ?? 'Logo'}
                                                                    width={100}
                                                                    height={100}
                                                                />
                                                            </div>
                                                        </Link>

                                                        <Link
                                                            href={getBrandUrl(position2?.url ?? '')}
                                                            className={cn(brandBorder, brandStyle)}>
                                                            <div className="flex h-[80px] w-[80px] items-center justify-center p-2.5">
                                                                <Image
                                                                    src={position2?.img ?? productPlaceholder}
                                                                    alt={position2?.label ?? 'Logo'}
                                                                    width={100}
                                                                    height={100}
                                                                />
                                                            </div>
                                                        </Link>
                                                    </div>
                                                </SwiperSlide>
                                            );
                                        })}
                                    </>
                                )}
                            </>

                            <div className="swiper-scrollbar"></div>
                        </Swiper>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PopularBrand;
